import { useMemo, useState, useSyncExternalStore } from 'react';
import { useT } from '@hana/i18n';
import './i18n';
import { IconArrowUp, IconPencil, IconDatabase, IconBell, IconSparkles, IconCloud, IconExternalLink, IconFilter, IconFolder, IconHome, IconPin, IconPlus, IconRefresh, IconSearch, IconTrash, IconUpload, IconUsers, IconClock, IconCopy, IconHourglass, IconWorld, IconShare, IconDots, IconArrowsSort, IconLayoutList, IconLayoutGrid, IconLayoutSidebarRight } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, StateView, useToast, type ScreenState } from '@hana/ui';
import { fileKind, filesInView, fmtTs, searchFiles, sizeLabel, sortFiles, type SortKey } from './logic';
import type { FileItem, FileView } from './types';
import { KIND_CLS } from './kindCls';
import { DocPanel, LibraryCols, NoticeBanner } from './LibraryTab';
import { MediaCols, MediaPanel } from './MediaTab';
import s from './files.module.css';

// Mint-Files (khổ 1536 / 1920), tab "Tệp của tôi": cột trái 212 (góc nhìn + ghim + dọn dẹp + dung lượng) · cột phải: thanh công cụ 56, truy cập nhanh, bảng tệp.
// Chỉ đọc: mọi nút ghi báo toast "Chưa mở ghi ở bản này". Tab Tài liệu công ty + Media: LibraryTab / MediaTab (?tab=my|lib|media).

const NAV: readonly { key: FileView; label: string; icon: typeof IconHome }[] = [
  { key: 'mine', label: 'fl.mine', icon: IconHome },
  { key: 'recent', label: 'fl.recent', icon: IconClock },
  { key: 'shared', label: 'fl.shared', icon: IconUsers },
  { key: 'myshared', label: 'fl.myshared', icon: IconShare },
];
const CLEAN: readonly { key: FileView; label: string; icon: typeof IconHome }[] = [
  { key: 'large', label: 'fl.large', icon: IconArrowsSort },
  { key: 'dupes', label: 'fl.dupes', icon: IconCopy },
  { key: 'stale', label: 'fl.stale', icon: IconHourglass },
  { key: 'external', label: 'fl.external', icon: IconWorld },
];
const CRUMB: Record<string, string> = { recent: 'fl.recent', shared: 'fl.shared', myshared: 'fl.myshared', large: 'fl.large', dupes: 'fl.dupes', stale: 'fl.stale', external: 'fl.external' };

const PH: Record<Tab, string> = { my: 'fl.phMy', lib: 'fl.phLib', media: 'fl.phMedia' };
const WIDE = '(min-width: 1800px)';
function useWide(): boolean {
  return useSyncExternalStore(
    (cb) => { const m = window.matchMedia(WIDE); m.addEventListener('change', cb); return () => m.removeEventListener('change', cb); },
    () => window.matchMedia(WIDE).matches,
    () => false,
  );
}

type Tab = 'my' | 'lib' | 'media';
function readTab(): Tab {
  const v = new URLSearchParams(location.search).get('tab');
  return v === 'lib' || v === 'media' ? v : 'my';
}

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

export function FilesPage() {
  const t = useT();
  const toast = useToast();
  const mine = useList('file', { limit: 5000 });
  const shared = useList('fileShared', { limit: 5000 });
  const docsQ = useList('libDoc', { limit: 500 });
  const albumsQ = useList('fileAlbum', { limit: 500 });
  const [tab, setTab] = useState<Tab>(readTab);
  const [libSel, setLibSel] = useState<number | null>(null);
  const [album, setAlbum] = useState<number | null>(null);
  const [noticeOff, setNoticeOff] = useState(false);
  const [view, setView] = useState<FileView>('mine');
  const [path, setPath] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [dense, setDense] = useState<'std' | 'dense'>('std');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'ts', dir: -1 });
  const [chk, setChk] = useState<Record<string, boolean>>({});
  const wide = useWide();
  const noWrite = () => toast({ msg: t('fl.noWrite') });

  const all = useMemo(() => mine.data?.items ?? [], [mine.data]);
  const sh = useMemo(() => shared.data?.items ?? [], [shared.data]);
  const forced = readState();
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (mine.isLoading) state = 'loading';
  else if (mine.isError) state = mine.error instanceof DataError && mine.error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';

  const docs = useMemo(() => docsQ.data?.items ?? [], [docsQ.data]);
  const albums = useMemo(() => albumsQ.data?.items ?? [], [albumsQ.data]);
  const other = tab === 'lib' ? docsQ : albumsQ;
  // Tab Tài liệu công ty / Media: trạng thái theo nguồn riêng của tab (OneDrive lỗi không kéo theo).
  let tabState: ScreenState = 'ready';
  if (forced) tabState = forced;
  else if (other.isLoading) tabState = 'loading';
  else if (other.isError) tabState = other.error instanceof DataError && other.error.code === 'noperm' ? 'noperm' : 'error';
  else if ((tab === 'lib' ? docs : albums).length === 0) tabState = 'empty';
  const cur = path[path.length - 1] ?? 'root';
  const rows = useMemo(() => {
    const base = filesInView(all, sh, view, view === 'mine' ? cur : 'root');
    return sortFiles(searchFiles(base, q), sort.key, sort.dir);
  }, [all, sh, view, cur, q, sort]);
  const count = (v: FileView) => filesInView(all, sh, v).length;
  const pinned = all.filter((f) => f.dir && f.pin);
  const quick = useMemo(() => filesInView(all, sh, 'recent').slice(0, 5), [all, sh]);
  const name = (id: string) => all.find((f) => f.id === id)?.n ?? '';
  const crumbs: [string, string][] = view === 'mine' || view.startsWith('pin:') ? [['root', t('fl.mine')], ...path.map((id): [string, string] => [id, name(id)])] : [[view, t(CRUMB[view] ?? '')]];
  const pick = (v: FileView, p: string[] = []) => { setView(v); setPath(p); setQ(''); setChk({}); };
  const open = (f: FileItem) => (f.dir ? pick('mine', view === 'mine' && !q ? [...path, f.id] : [f.id]) : noWrite());
  const sortBy = (key: SortKey) => setSort((o) => (o.key === key ? { key, dir: o.dir === 1 ? -1 : 1 } : { key, dir: key === 'n' ? 1 : -1 }));
  const arrow = (key: SortKey) => (sort.key === key ? (sort.dir === 1 ? '↑' : '↓') : '');
  const ready = state === 'ready';
  const my = tab === 'my';
  const ok = (my ? state : tabState) === 'ready' || (my ? state : tabState) === 'empty';
  const curDoc = docs.find((d) => d.id === libSel) ?? null;
  const curAlbum = albums.find((a) => a.id === album) ?? null;
  const pickTab = (k: Tab) => { setTab(k); setQ(''); setLibSel(null); setAlbum(null); };
  const rowH = dense === 'dense' ? 40 : 48;
  const checked = Object.values(chk).filter(Boolean).length;
  const allChk = rows.length > 0 && rows.every((r) => chk[r.id]);

  return (
    <Page
      kind="tool"
      dock={
        wide && ok && tab === 'lib' ? (
          <div className={s.dockIn} role="region" aria-label={t('fl.detail')}>
            {curDoc ? <DocPanel doc={curDoc} noWrite={noWrite} /> : <p className={s.dockHint}>{t('fl.pickDoc')}</p>}
          </div>
        ) : wide && ok && tab === 'media' ? (
          <div className={s.dockIn} role="region" aria-label={t('fl.detail')}><MediaPanel album={curAlbum} noWrite={noWrite} /></div>
        ) : wide && ok ? (
          <div className={s.dockIn} role="region" aria-label={t('fl.detail')}>
            <p className={s.dockHint}>{t('fl.pickFile')}</p>
            <div className={s.dockCard}>
              <h2>{t('fl.thisFolder')}</h2>
              <dl>
                <dt>{t('fl.folder')}</dt><dd>{view === 'mine' ? (cur === 'root' ? t('fl.mine') : name(cur)) : t(CRUMB[view] ?? '')}</dd>
                <dt>{t('fl.items')}</dt><dd>{rows.length}</dd>
                <dt>{t('fl.size')}</dt><dd>{sizeLabel(rows.reduce((a, r) => a + r.sz, 0))}</dd>
                <dt>{t('fl.lastEdit')}</dt><dd>{rows[0] ? fmtTs([...rows].sort((a, b) => b.ts.localeCompare(a.ts))[0]!.ts) : '—'}</dd>
              </dl>
              <Button variant="ask" onClick={noWrite}>{t('fl.askSum')}</Button>
            </div>
          </div>
        ) : undefined
      }
    >
      <div className={s.root}>
      <PageHeader
        title={t('fl.title')}
        tabs={
          <>
            <Segmented
              label={t('fl.groups')}
              value={tab}
              items={[{ key: 'my', label: t('fl.tabMy') }, { key: 'lib', label: t('fl.tabLib'), n: docs.filter((d) => !d.deleted).length }, { key: 'media', label: t('fl.tabMedia'), n: albums.length }]}
              onChange={pickTab}
            />
            <label className={s.find}>
              <IconSearch size={16} aria-hidden="true" />
              <input aria-label={t(PH[tab])} placeholder={t(PH[tab])} value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Segmented label={t('fl.density')} kind="toggle" value={dense} onChange={setDense} items={[{ key: 'std', label: t('fl.dStd') }, { key: 'dense', label: t('fl.dDense') }]} />
          </>
        }
        actions={
          <>
            <HeaderTool label={t('fl.notif3')} icon={IconBell} n={3} />
            {!wide && <HeaderTool label={t('fl.detailPanel')} icon={IconLayoutSidebarRight} onClick={noWrite} />}
            <Button variant="ask" icon={<IconSparkles size={16} />} onClick={noWrite}>Ask HANA</Button>
            {(my ? state : tabState) !== 'noperm' && (
              <Button variant="primary" icon={tab === 'my' ? <IconUpload size={16} /> : <IconPlus size={16} />} onClick={noWrite}>
                {tab === 'my' ? t('fl.upload') : tab === 'lib' ? t('fl.addDoc') : curAlbum ? t('fl.uploadPhoto') : t('fl.newAlbum')}
              </Button>
            )}
          </>
        }
      />
      {tab === 'lib' && tabState === 'ready' && !noticeOff && <NoticeBanner n={3} onScan={noWrite} onHide={() => setNoticeOff(true)} />}
      {tab === 'lib' && <LibraryCols docs={docs} banner={tabState === 'ready' && !noticeOff} state={tabState} q={q} rowH={rowH} wide={wide} sel={libSel} setSel={setLibSel} noWrite={noWrite} retry={() => void docsQ.refetch()} />}
      {tab === 'media' && <MediaCols albums={albums} state={tabState} q={q} album={album} setAlbum={setAlbum} noWrite={noWrite} retry={() => void albumsQ.refetch()} />}
      {tab === 'my' && (
      <div className={s.cols}>
        <section className={s.c1} aria-label={t('fl.oneDriveDirs')}>
          {NAV.map((n) => (
            <button key={n.key} type="button" className={s.nav} aria-pressed={view === n.key && !q} onClick={() => pick(n.key)}>
              <n.icon size={17} aria-hidden="true" />
              <span className={s.navK}>{t(n.label)}</span>
              {n.key !== 'mine' && n.key !== 'recent' && ok && <span className={s.navN}>{n.key === 'shared' ? sh.length : count(n.key)}</span>}
            </button>
          ))}
          {pinned.length > 0 && <div className={s.head}>{t('fl.pinned')}</div>}
          {pinned.map((f) => (
            <button key={f.id} type="button" className={s.nav} aria-pressed={view === 'mine' && path[0] === f.id && path.length === 1} onClick={() => pick('mine', [f.id])}>
              <IconPin size={17} aria-hidden="true" />
              <span className={s.navK}>{f.n}</span>
            </button>
          ))}
          <div className={s.head}>{t('fl.clean')}</div>
          {CLEAN.map((n) => (
            <button key={n.key} type="button" className={s.nav} aria-pressed={view === n.key && !q} onClick={() => pick(n.key)}>
              <n.icon size={17} aria-hidden="true" />
              <span className={s.navK}>{t(n.label)}</span>
              {ok && <span className={s.navN}>{count(n.key)}</span>}
            </button>
          ))}
          <div className={s.grow} />
          <div className={s.quota}>
            <div className={s.quotaRow}><span>{t('fl.quota')}</span><b>812 GB / 1 TB</b></div>
            <div className={s.bar} role="progressbar" aria-label={t('fl.quotaAria')} aria-valuenow={79} aria-valuemin={0} aria-valuemax={100}><div style={{ width: '79%' }} /></div>
            <div className={s.quotaSub}>{t('fl.quotaSub')}</div>
          </div>
          <a className={s.ext} href="https://www.office.com/launch/onedrive" target="_blank" rel="noreferrer"><IconTrash size={16} aria-hidden="true" /><span>{t('fl.trashOd')}</span><IconExternalLink size={14} aria-hidden="true" /></a>
          <a className={s.ext} href="https://www.office.com/launch/onedrive" target="_blank" rel="noreferrer"><IconCloud size={16} aria-hidden="true" /><span>{t('fl.openOd')}</span><IconExternalLink size={14} aria-hidden="true" /></a>
          <button type="button" className={`${s.ext} ${s.adm}`} onClick={noWrite}><IconUsers size={16} aria-hidden="true" /><span>{t('fl.empOd')}</span><em>{t('fl.admin')}</em></button>
        </section>

        <section className={s.c2} aria-label={t('fl.list')}>
          <div className={s.bar56}>
            {state !== 'noperm' && (
              <Button variant="secondary" icon={<IconPlus size={16} />} onClick={noWrite}>{t('fl.new')}</Button>
            )}
            <Button aria-label={t('fl.up')} title={t('fl.up')} variant="soft" icon={<IconArrowUp size={16} />} disabled={view !== 'mine' || path.length === 0} onClick={() => setPath(path.slice(0, -1))} />
            <Button aria-label={t('fl.refresh')} title={t('fl.refresh')} variant="soft" icon={<IconRefresh size={16} />} onClick={() => { void mine.refetch(); void shared.refetch(); }} />
            <nav className={s.crumbs} aria-label={t('fl.path')}>
              {crumbs.map(([id, k], i) => (
                <span key={id} className={s.crumbWrap}>
                  {i > 0 && <span aria-hidden="true" className={s.sep}>›</span>}
                  <button type="button" className={s.crumb} aria-current={i === crumbs.length - 1} onClick={() => pick(view.startsWith('pin:') ? 'mine' : view, id === 'root' ? [] : path.slice(0, path.indexOf(id) + 1))}>{k}</button>
                </span>
              ))}
            </nav>
            <span className={s.grow} />
            <Button variant="soft" icon={<IconFilter size={16} />} onClick={noWrite}>{t('fl.filter')}</Button>
            <div className={s.lay} role="group" aria-label={t('fl.layout')}>
              <button type="button" aria-label={t('fl.detail')} title={t('fl.detail')} aria-pressed="true"><IconLayoutList size={16} aria-hidden="true" /></button>
              <button type="button" aria-label={t('fl.grid')} title={t('fl.grid')} aria-pressed="false" onClick={noWrite}><IconLayoutGrid size={16} aria-hidden="true" /></button>
            </div>
          </div>
          <div className={s.scroll}>
            <StateView
              state={ready ? 'ready' : state}
              title={state === 'error' ? t('fl.errTitle') : state === 'noperm' ? t('fl.npTitle') : t('fl.emptyDir')}
              desc={state === 'error' ? t('fl.errDesc') : state === 'noperm' ? t('fl.npDesc') : undefined}
              action={state === 'error' ? <Button onClick={() => void mine.refetch()}>{t('fl.reconnect')}</Button> : state === 'noperm' ? <Button variant="primary" onClick={noWrite}>{t('fl.grant')}</Button> : undefined}
            >
              {view === 'mine' && !q && path.length === 0 && quick.length > 0 && (
                <div className={s.quick}>
                  <div className={s.head0}>{t('fl.quick')}</div>
                  <div className={s.qGrid}>
                    {quick.map((f) => {
                      const [k] = fileKind(f.n);
                      return (
                        <button key={f.id} type="button" className={s.qCard} onClick={() => open(f)}>
                          <span aria-hidden="true" className={`${s.kind} ${KIND_CLS[k]}`}>{k}</span>
                          <span className={s.qTx}><span className={s.qN}>{f.n}</span><span className={s.qW}>{fmtTs(f.ts).slice(0, 6)} {fmtTs(f.ts).slice(-5)}</span></span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className={s.th} role="row">
                <span className={s.w20}><input type="checkbox" aria-label={t('fl.selAll')} checked={allChk} onChange={() => setChk(allChk ? {} : Object.fromEntries(rows.map((r) => [r.id, true])))} /></span>
                <button type="button" className={s.thK} onClick={() => sortBy('n')}>{t('fl.colName')} {arrow('n')}</button>
                <button type="button" className={`${s.thK} ${s.w150}`} onClick={() => sortBy('ts')}><IconPencil size={16} stroke={1.8} aria-hidden />{t('fl.colEdited')} {arrow('ts')}</button>
                <span className={s.w96}>{t('fl.colBy')}</span>
                <span className={s.w70}>{t('fl.colType')}</span>
                <button type="button" className={`${s.thK} ${s.w84} ${s.right}`} onClick={() => sortBy('sz')}><IconDatabase size={16} stroke={1.8} aria-hidden />{t('fl.colSize')} {arrow('sz')}</button>
                <span className={s.w32} />
              </div>
              {rows.length === 0 && <div className={s.none}>{t('fl.noMatch')}</div>}
              {rows.map((f) => {
                const [k, kn] = f.dir ? (['FILE', t('fl.dir')] as const) : fileKind(f.n);
                return (
                  <div key={f.id} role="row" aria-selected={!!chk[f.id]} className={s.row} style={{ height: rowH }}>
                    <span className={s.w20}><input type="checkbox" aria-label={t('fl.select', { n: f.n })} checked={!!chk[f.id]} onChange={() => setChk({ ...chk, [f.id]: !chk[f.id] })} /></span>
                    <button type="button" className={s.name} onClick={() => open(f)}>
                      {f.dir ? <IconFolder size={26} aria-hidden="true" className={s.dir} /> : <span aria-hidden="true" className={`${s.kind} ${KIND_CLS[k]}`}>{k}</span>}
                      <span className={s.nameTx}>
                        <span className={s.nameLine}>
                          <span className={s.nameN} style={{ fontWeight: f.dir ? 600 : 500 }}>{f.n}</span>
                          {f.sh && <span className={`${s.shc} ${f.sh === 'anyone' ? s.shAny : ''}`}>{f.sh === 'anyone' ? t('fl.anyone') : t('fl.org')}</span>}
                          {f.pin && f.dir && <span className={s.pinTx}>{t('fl.pinTx')}</span>}
                        </span>
                        {q && f.txt && <span className={s.snip}>{t('fl.txtHit', { s: f.txt.slice(0, 70) })}</span>}
                        {!q && view === 'shared' && f.owner && <span className={s.snip}>{t('fl.sharedBy', { n: f.owner })}</span>}
                      </span>
                    </button>
                    <span className={`${s.w150} ${s.dim}`}>{fmtTs(f.ts)}</span>
                    <span className={`${s.w96} ${s.dim}`}>{f.by}</span>
                    <span className={`${s.w70} ${s.dim}`}>{kn === 'Ảnh' ? t('fl.kImg') : kn === 'Tệp' ? t('fl.kFile') : kn}</span>
                    <span className={`${s.w84} ${s.right} ${s.dim}`}>{f.dir ? t('fl.itemsN', { n: f.cnt ?? 0 }) : sizeLabel(f.sz)}</span>
                    <Button className={s.act} aria-label={t('fl.actWith', { n: f.n })} variant="ghost" icon={<IconDots size={18} />} onClick={noWrite} />
                  </div>
                );
              })}
              {checked > 0 && <div className={s.sel}>{t('fl.chosen', { n: checked })}</div>}
            </StateView>
          </div>
          {wide && ok && <div className={s.foot}><span>{t('fl.itemsFoot', { n: rows.length })}</span><span><IconRefresh size={13} aria-hidden="true" /> 09:12</span></div>}
        </section>
      </div>
      )}
      </div>
    </Page>
  );
}
